import { useState, useContext, useEffect } from 'react';
import { GlCodingContext } from "../../../contexts/contexts";
import AccountConfigTable from "./AccountConfigTable";
import {
    Container,
    SpaceBetween,
    Grid,
    Header,
    Button,
    Input,
    FormField,
    Form, 
    StatusIndicator, 
    Select, 
    FlashbarProps,
    Toggle,
    Modal,
    Box,
    Alert,
    Table
} from "@amzn/awsui-components-react-v3/polaris";
import {
    AccountConfigurationProps,
    AccountTableObject, ChargeGroupConfigProps,
    ShipmentAccountObject, GlCodeDropdownProps,
    GLCodeErrorObject, GLCodeObject,
    UpdateSimpleRuleArgsPayload
} from "../../../interfaces";
import constants from "../../../constants/strings";
import { AxiosError, AxiosResponse } from "axios";
import { addTypeToRuleArguments, placeGetRequest, placePostRequest, placePostRequestAsync, decompressData } from "../../../utils";
import {isDropdownFieldValid, isFieldValid} from "../../../utils/errorChecks";
import AccountConfigPopup from "./AccountConfigPopup";
import ChargeGroupConfigsAdd from "./ChargeGroupConfigsAdd";
import {
    DEFAULT_ACCOUNT_TABLE_OBJECT,
    DEFAULT_BUSINESS_TYPE_OPTION,
    DEFAULT_CONTRACT_TYPE_OPTION,
    DEFAULT_COUNTRY_CODE_OPTION,
    DEFAULT_GL_CODE_OPTION,
    DEFAULT_GL_CODE_OBJECT,
    DEFAULT_GL_ERROR_OBJECT,
    DEFAULT_EMPTY_FLASH_MESSAGE,
    DEFAULT_INPUT_ACCOUNT_TABLE_OBJECT
} from "../../../constants/objects";
import {getErrorFlashMessageProps, getErrorMessage} from "../../../utils/errorMessage";
import MatrixFlashMessage, {MatrixFlashMessageProps} from "../CommonComponents/MatrixFlashMessage";
import {Badge, SelectProps} from "@amzn/awsui-components-react-v3";
import InvoiceMatchPropertyConfigPopup from "./InvoiceMatchPropertyConfigPopup";


// Default enabled-status for a newly added account. Adding an account is intended to activate it, so the
// toggle defaults to enabled. Kept as a named constant so useState and setDefaultValues stay in sync.
const DEFAULT_ENABLE_ACCOUNT_NUMBER = true;

function AccountConfiguration(props: AccountConfigurationProps) {
    const [accountNumber, setAccountNumber] = useState<string>(constants.EMPTY_STRING);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [hideAccountTable, setHideAccountTable] = useState<boolean>(false);
    const [accountNumbers, setAccountNumbers] = useState<string>(constants.EMPTY_STRING);
    const [businessType, setBusinessType] = useState<SelectProps.Option>(DEFAULT_BUSINESS_TYPE_OPTION);
    const [contractType, setContractType] = useState<SelectProps.Option>(DEFAULT_CONTRACT_TYPE_OPTION);
    const [countryCode, setCountryCode] = useState<SelectProps.Option>(DEFAULT_COUNTRY_CODE_OPTION);
    const [makeSearchCall, setMakeSearchCall] = useState<boolean>(false);
    const [searchAccountNumber, setSearchAccountNumber] = useState<boolean>(false);
    const [searchedTableData, setSearchedTableData] = useState<AccountTableObject[]>([]);
    const [accountNumbersError, setAccountNumbersError] = useState<string>(constants.EMPTY_STRING);
    const [businessTypeError, setBusinessTypeError] = useState<string>(constants.EMPTY_STRING);
    const [contractTypeError, setContractTypeError] = useState<string>(constants.EMPTY_STRING);
    const [countryCodeError, setCountryCodeError] = useState<string>(constants.EMPTY_STRING);
    const [showAccountDetails, setShowAccountDetails] = useState<boolean>(false);
    const [showIMPSDetails, setShowIMPSDetails] = useState<boolean>(false);
    const [popupAccountObject, setPopupAccountObject] = useState<AccountTableObject>(DEFAULT_ACCOUNT_TABLE_OBJECT);
    const [chargeGroupConfigs, setChargeGroupConfigs] = useState<ChargeGroupConfigProps[]>([]);
    const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [message, setMessage] = useState<FlashbarProps.MessageDefinition>(DEFAULT_EMPTY_FLASH_MESSAGE);
    const [secondaryMessages, setSecondaryMessages] = useState<MatrixFlashMessageProps>({});
    const [toggleStatusMessage, setToggleStatusMessage] = useState<FlashbarProps.MessageDefinition>(DEFAULT_EMPTY_FLASH_MESSAGE);
    const { gLCodingRuleConfigurationMap, gLCodingRulePossibleValues = {}, gLCodingRuleTreeId } = useContext(GlCodingContext);
    const [glCodeDropdownData, setGlCodeDropdownData] = useState<GlCodeDropdownProps[]>([]);
    const [glSelectedData, setGlSelectedData] = useState<GLCodeObject>(DEFAULT_GL_CODE_OBJECT);
    const [glSelectionError, setGlSelectionError] = useState<GLCodeErrorObject>(DEFAULT_GL_ERROR_OBJECT);
    const [enableAccountNumber, setEnableAccountNumber] = useState<boolean>(DEFAULT_ENABLE_ACCOUNT_NUMBER);
    const [errorModalMessage, setErrorModalMessage] = useState<string>(constants.EMPTY_STRING);
    const [conflictRetryMessage, setConflictRetryMessage] = useState<string>(constants.EMPTY_STRING);
    const [modalSeverity, setModalSeverity] = useState<"error" | "warning">("error");
    const [conflictingAccounts, setConflictingAccounts] = useState<{accountNumber: string, existingAccountType: string, isDisabled: boolean}[]>([]);
    const [isDisableAllLoading, setIsDisableAllLoading] = useState<boolean>(false);
    const [showManualDisable, setShowManualDisable] = useState<boolean>(false);
    const [manualDisableSelections, setManualDisableSelections] = useState<Map<string, boolean>>(new Map());
    
    useEffect(() => {
        const data: GlCodeDropdownProps[] = [];
        const gLCodingRuleKeys = Object.keys(gLCodingRulePossibleValues);
        if (gLCodingRuleKeys.length) {
            gLCodingRuleKeys.forEach(ruleConfigurationKey => {
                if (ruleConfigurationKey.startsWith("account")) {
                    data.push({
                        rule: ruleConfigurationKey?.split("Map")?.[0],
                        values: gLCodingRulePossibleValues[ruleConfigurationKey]
                    });
                }
            });
            setGlCodeDropdownData(data);
        }
    }, [gLCodingRulePossibleValues]);

    const getTableRowData = (data: ShipmentAccountObject) => {
        const inputAccountTableObject: AccountTableObject = props.inputAccountConfigurationMap.get(data.accountNumber) || DEFAULT_INPUT_ACCOUNT_TABLE_OBJECT;
        const tableRowData: AccountTableObject = {
            accountNumber: data.accountNumber,
            status: inputAccountTableObject.status,
            businessType: inputAccountTableObject.businessType,
            contractType: inputAccountTableObject.contractType,
            countryCode: inputAccountTableObject.countryCode,
            chargeGroupConfigMap: inputAccountTableObject.chargeGroupConfigMap,
            InvoiceMatchPropertyList: inputAccountTableObject.InvoiceMatchPropertyList,
            version: inputAccountTableObject.version
        };
        return tableRowData;
    }

    function getDataForSearchedAccount() {
        let _searchedTableData: AccountTableObject[] = [];
        const data = {
            "operation": constants.GET_ACCOUNT_CONFIGURATION,
            "request_body": {
                "scac": props.scac,
                "shipmentAccountType": props.accountType,
                "accountNumber": accountNumber
            }
        }
        const onSuccess = (res: AxiosResponse) => {
            const accountConfigOutput = decompressData(res.data["accountConfigurationOutput"]);
            const searchedData: ShipmentAccountObject[] = accountConfigOutput["accountConfigurations"]["shipmentAccountList"] || [];
            searchedData.forEach(data => {
                _searchedTableData.push(getTableRowData(data))
            });
            setSearchedTableData(_searchedTableData);
            setLoading(false);
        }
        const onError = (err: AxiosError) => {
            setLoading(false);
        }
        if (makeSearchCall) {
            placeGetRequest(data, onSuccess, onError);
            setMakeSearchCall(false);
        }
        return searchedTableData;
    }

    function getTableData() {
        const startIndex: number = props.pageToken * constants.DEFAULT_PAGE_SIZE;
        const endIndex: number = Math.min(startIndex + constants.DEFAULT_PAGE_SIZE, props.shipmentAccountList.length);
        let tableData: AccountTableObject[] = [];
        props.shipmentAccountList.slice(startIndex, endIndex).forEach(data => {
            tableData.push(getTableRowData(data));
        });
        return tableData;
    }

    function filterAccountNumber() {
        setLoading(true);
        setSearchAccountNumber(true);
        setMakeSearchCall(true);
    }

    function setDefaultValues() {
        setAccountNumbers(constants.EMPTY_STRING);
        setBusinessType(DEFAULT_BUSINESS_TYPE_OPTION);
        setContractType(DEFAULT_CONTRACT_TYPE_OPTION);
        setCountryCode(DEFAULT_COUNTRY_CODE_OPTION);
        setMessage(DEFAULT_EMPTY_FLASH_MESSAGE);
        setGlSelectedData(DEFAULT_GL_CODE_OBJECT);
        setGlSelectionError(DEFAULT_GL_ERROR_OBJECT);
        setSecondaryMessages({});
        // Reset the enabled toggle too, so a value chosen for one add does not persist into the next
        // add within the same session.
        setEnableAccountNumber(DEFAULT_ENABLE_ACCOUNT_NUMBER);
    }

    function setGlSelectionErrorWrapper(key: string, value = constants.EMPTY_STRING) {
        setGlSelectionError((lastVal) => { return { ...lastVal, [key]: value } });
    }

    const [showSaveConfirmModal, setShowSaveConfirmModal] = useState<boolean>(false);

    function checkAccountMappingBatch(scac: string, accountNumbersList: string[], targetAccountType: string): Promise<{accountNumber: string, type: string | null, isDisabled: boolean}[]> {
        return new Promise((resolve, reject) => {
            placePostRequest(
                {
                    "operation": constants.BATCH_GET_ACCOUNT_TYPE,
                    "request_body": {
                        "scac": scac,
                        "shipmentAccountType": targetAccountType,
                        "accountNumbers": accountNumbersList
                    }
                },
                (res: AxiosResponse) => {
                    // The API sometimes returns the body as a JSON string, and it
                    // wraps payloads under an operation-specific key (as every other
                    // call in this file does, e.g. res.data["accountConfigurationOutput"]).
                    // Parse defensively and look for batchResults at the top level or
                    // one level nested so validation is not silently skipped.
                    try {
                        const parsed = typeof res.data === "string" ? JSON.parse(res.data) : res.data;

                        let batchResults: any[] | undefined;
                        if (Array.isArray(parsed?.batchResults)) {
                            batchResults = parsed.batchResults;
                        } else if (parsed && typeof parsed === "object") {
                            // Look one level deep for a batchResults array under any output key.
                            for (const value of Object.values(parsed)) {
                                if (value && typeof value === "object" && Array.isArray((value as any).batchResults)) {
                                    batchResults = (value as any).batchResults;
                                    break;
                                }
                            }
                        }

                        if (!batchResults) {
                            // Do not silently proceed to save — surface a failure so the
                            // conflict validation is not bypassed due to an unexpected shape.
                            reject(new Error("Unexpected response shape from BATCH_GET_ACCOUNT_TYPE: missing batchResults"));
                            return;
                        }

                        resolve(batchResults.map((r: any) => ({
                            accountNumber: r.accountNumber,
                            type: r.type || null,
                            isDisabled: r.isDisabled || false
                        })));
                    } catch (parseErr) {
                        // A malformed JSON string would otherwise throw inside this async callback,
                        // leaving the Promise unsettled forever (the awaiting caller — validateAndSave —
                        // would hang with the Confirm spinner stuck). Reject so validation surfaces a
                        // failure instead of bypassing the conflict check.
                        reject(parseErr);
                    }
                },
                (err: AxiosError) => {
                    reject(err);
                }
            );
        });
    }

    async function validateBeforeSave() {
        // Field validations
        let glErrorExists = false;
        glCodeDropdownData?.forEach((glCode) => {
            if (!isDropdownFieldValid(glSelectedData[glCode.rule], DEFAULT_GL_CODE_OPTION, constants.REQUIRED_GENERIC_ERROR_TEXT, (msg) => { setGlSelectionErrorWrapper(glCode.rule, msg) }))
                glErrorExists = true;
        });
        if (!isFieldValid(accountNumbers, constants.ACCOUNT_NUMBERS_ERROR_TEXT, setAccountNumbersError)
            || !isDropdownFieldValid(businessType, DEFAULT_BUSINESS_TYPE_OPTION, constants.BUSINESS_TYPE_ERROR_TEXT, setBusinessTypeError)
            || !isDropdownFieldValid(contractType, DEFAULT_CONTRACT_TYPE_OPTION, constants.CONTRACT_TYPE_ERROR_TEXT, setContractTypeError)
            || !isDropdownFieldValid(countryCode, DEFAULT_COUNTRY_CODE_OPTION, constants.COUNTRY_CODE_ERROR_TEXT, setCountryCodeError)
            || glErrorExists) {
            return;
        }
        if (!businessType.value || !contractType.value || !countryCode.value) {
            return;
        }

        // Show confirm modal immediately (no API call yet)
        setShowSaveConfirmModal(true);
    }

    async function validateAndSave() {
        setIsValidating(true);

        // Check all accounts for conflicts with other SATs in a single batch call
        const accountNumbersList = [...new Set(accountNumbers.split(",").map((a) => a.trim()).filter((a) => a !== constants.EMPTY_STRING))];

        try {
            const results = await checkAccountMappingBatch(props.scac, accountNumbersList, props.accountType);

            // Combine enabled and disabled mappings into a single conflicts list.
            // Both are surfaced to the user with a toggle so they can choose to
            // keep the existing mapping or override it.
            const conflicts: {accountNumber: string, existingAccountType: string, isDisabled: boolean}[] = [];
            const seen = new Set<string>();

            results.forEach(({ accountNumber, type, isDisabled }) => {
                if (seen.has(accountNumber)) return;
                seen.add(accountNumber);
                if (type) {
                    conflicts.push({ accountNumber, existingAccountType: type, isDisabled });
                }
            });

            setIsValidating(false);

            if (conflicts.length > 0) {
                setShowSaveConfirmModal(false);
                setConflictingAccounts(conflicts);
            } else {
                saveAddedAccounts();
            }
        } catch (err: any) {
            setIsValidating(false);
            setShowSaveConfirmModal(false);
            setConflictingAccounts([]);
            setModalSeverity("error");
            setErrorModalMessage(
                err?.response?.data?.errorMessage || "Failed to validate accounts. Please try again."
            );
        }
    }

    async function disableAllConflictingAndProceed() {
        setIsDisableAllLoading(true);
        setConflictRetryMessage(constants.EMPTY_STRING);

        // Accounts already disabled in their existing SAT can be added as they are, so only enabled ones are disabled.
        const accountsToDisable = conflictingAccounts.filter(({ isDisabled }) => !isDisabled);
        const failures = accountsToDisable.length > 0 ? await disableConflictingAccounts(accountsToDisable) : [];
        const failedAccounts = failures.map(({ accountNumber }) => accountNumber);

        setIsDisableAllLoading(false);

        if (failedAccounts.length > 0) {
            // Do NOT roll back the ones that already succeeded — UPDATE_RULE_REMOVE_ACCOUNT_NUMBER is
            // idempotent, so re-running it on an already-disabled account is a safe no-op. Instead of
            // aborting on the first failure and forcing a full restart, we attempted every account and now
            // keep only the ones that failed in the modal, so the user can retry just those.
            //
            // Surface the failure INLINE within the conflict modal (via conflictRetryMessage) rather than
            // opening the separate error modal — the conflict modal stays visible (conflictingAccounts is
            // still non-empty), so opening a second modal would stack two dialogs.
            setConflictingAccounts(conflictingAccounts.filter(
                ({ accountNumber }) => failedAccounts.includes(accountNumber)));
            setConflictRetryMessage(
                getDisableFailureMessage(failures, accountsToDisable.length, "Override All & Proceed"));
            return;
        }

        // Save directly instead of re-opening the confirm modal: its Confirm re-runs validateAndSave, and the
        // batch check still reports the now-disabled mappings as conflicts, which re-opened this modal in a loop.
        // The backend allows adding accounts that are disabled in another SAT.
        setConflictRetryMessage(constants.EMPTY_STRING);
        setConflictingAccounts([]);
        saveAddedAccounts();
    }

    function handleDisableManuallyClick() {
        // Initialize toggle selections — all default to their current enabled status (true = enabled)
        const selections = new Map<string, boolean>();
        conflictingAccounts.forEach(({ accountNumber }) => {
            selections.set(accountNumber, true); // default: enabled (current state)
        });
        setManualDisableSelections(selections);
        setShowManualDisable(true);
    }

    async function disableSelectedAndProceed() {
        setIsDisableAllLoading(true);
        setConflictRetryMessage(constants.EMPTY_STRING);
        // Toggle OFF = override. Accounts already disabled in their existing SAT are added without disabling them again.
        const accountsToDisable = conflictingAccounts.filter(
            ({ accountNumber, isDisabled }) => !manualDisableSelections.get(accountNumber) && !isDisabled
        );

        // Remove skipped accounts (still enabled) from the input list
        const skippedAccounts = conflictingAccounts
            .filter(({ accountNumber }) => manualDisableSelections.get(accountNumber))
            .map(({ accountNumber }) => accountNumber);

        const currentList = accountNumbers.split(",").map((a) => a.trim()).filter((a) => a !== constants.EMPTY_STRING);
        const filteredList = currentList.filter(acc => !skippedAccounts.includes(acc));

        // Keeping every conflicting account is a valid choice: proceed with only the non-conflicting accounts
        // (if any) instead of forcing at least one override.
        if (filteredList.length === 0) {
            setIsDisableAllLoading(false);
            setConflictRetryMessage(constants.EMPTY_STRING);
            setShowManualDisable(false);
            setConflictingAccounts([]);
            setMessage({
                type: "info",
                content: `No accounts were added to ${props.accountType}. All conflicting account(s) were kept in their existing account type.`,
                dismissible: true,
                onDismiss: () => setMessage(DEFAULT_EMPTY_FLASH_MESSAGE)
            });
            return;
        }

        const failures = accountsToDisable.length > 0 ? await disableConflictingAccounts(accountsToDisable) : [];
        const failedAccounts = failures.map(({ accountNumber }) => accountNumber);

        setIsDisableAllLoading(false);

        if (failedAccounts.length > 0) {
            // Attempt-all-then-report: keep only the accounts that failed to disable (still toggled to
            // 'Override') so the user can retry just those. Disabling is idempotent, so the accounts that
            // already succeeded do not need to be rolled back.
            //
            // Surface the failure INLINE within the conflict modal (via conflictRetryMessage) rather than
            // opening the separate error modal, which would stack a second dialog over the still-open
            // conflict modal.
            setConflictingAccounts(accountsToDisable.filter(
                ({ accountNumber }) => failedAccounts.includes(accountNumber)));
            setConflictRetryMessage(
                getDisableFailureMessage(failures, accountsToDisable.length, "Confirm & Proceed"));
            return;
        }

        setConflictRetryMessage(constants.EMPTY_STRING);
        setAccountNumbers(filteredList.join(", "));
        setShowManualDisable(false);
        setConflictingAccounts([]);
        saveAddedAccounts(filteredList);
    }

    /**
     * Disables the existing mapping for each conflicting account. Instead of stopping at the first failure,
     * every account is attempted (in parallel) and the account numbers that failed are collected and returned.
     * This makes retries non-incremental — a failed attempt does not force the user to restart from scratch;
     * they can retry only the accounts that failed. UPDATE_RULE_REMOVE_ACCOUNT_NUMBER is idempotent, so the
     * accounts that already succeeded are safe to leave as-is (no rollback required).
     *
     * @param conflicts the conflicting accounts to disable
     * @returns the accounts that failed to disable, with the error returned for each (empty if all succeeded)
     */
    async function disableConflictingAccounts(
        conflicts: {accountNumber: string, existingAccountType: string, isDisabled: boolean}[]
    ): Promise<{accountNumber: string, error: string}[]> {
        const outcomes = await Promise.all(
            conflicts.map((conflict) => new Promise<{accountNumber: string, error?: string}>((resolve) => {
                placePostRequest(
                    {
                        "operation": constants.UPDATE_RULE_REMOVE_ACCOUNT_NUMBER,
                        "request_body": {
                            "scac": props.scac,
                            "shipmentAccountType": conflict.existingAccountType,
                            "accountNumber": conflict.accountNumber
                        }
                    },
                    () => resolve({ accountNumber: conflict.accountNumber }),
                    (err: AxiosError) => resolve({ accountNumber: conflict.accountNumber, error: getErrorMessage(err).trim() })
                );
            }))
        );

        return outcomes
            .filter((outcome) => outcome.error !== undefined)
            .map((outcome) => ({ accountNumber: outcome.accountNumber, error: outcome.error! }));
    }

    // Names each failed account with the reason it failed, and only mentions other accounts succeeding when some did.
    function getDisableFailureMessage(failures: {accountNumber: string, error: string}[], attempted: number,
                                      retryButton: string): string {
        const details = failures.map(({ accountNumber, error }) => `${accountNumber} (${error})`).join("; ");
        const othersSucceeded = attempted > failures.length
            ? " The other selected account(s) were disabled successfully."
            : "";
        return `Failed to disable account(s): ${details}.${othersSucceeded} `
            + `Click '${retryButton}' again to retry only the failed account(s).`;
    }

    async function  saveAddedAccounts(accountNumbersOverride?: string[]) {
        setIsSaveLoading(true);

        const inputAccountConfigurationMaps = props.inputAccountConfigurationMap;
        const accountNumbersList = accountNumbersOverride ?? accountNumbers.split(",").map((accountNumber) => accountNumber.trim())
            .filter((accountNumber) => (accountNumber != constants.EMPTY_STRING));
        let inputVersionMap: Map<string, string> = new Map<string, string>();

        /*
            To update the existing Business Type, Contract Type and country Code, we need existing version of account number to call the API.
            So if the invoice account number already exists, then add the version value to map(inputVersionMap) and pass it to backend lambda.
        */
        accountNumbersList.forEach((accountNumber) => {
            if(inputAccountConfigurationMaps.has(accountNumber)) {
                const inputAccountTableObject: AccountTableObject = inputAccountConfigurationMaps.get(accountNumber)!;
                inputVersionMap.set(accountNumber, inputAccountTableObject.version);
            }
        });

        const saveGLDetails = async () => {
            for (const ruleConfigurationKey of Object.keys(gLCodingRuleConfigurationMap)) {
                if (ruleConfigurationKey.startsWith("account")) {
                    const ruleArgumentsMap = JSON.parse(JSON.stringify(gLCodingRuleConfigurationMap[ruleConfigurationKey].ruleArgumentMap));
                    const accountNumberValueMap: any = {};
                    const ruleConfigurationKeyName = `${ruleConfigurationKey}Map`;
                    accountNumbersList.forEach((accountNo: string) => accountNumberValueMap[accountNo] = glSelectedData[ruleConfigurationKey]?.value);
                    ruleArgumentsMap[ruleConfigurationKeyName].value = { ...ruleArgumentsMap[ruleConfigurationKeyName]?.value, ...accountNumberValueMap };
                    addTypeToRuleArguments(ruleArgumentsMap)

                    const glData: UpdateSimpleRuleArgsPayload = {
                        "operation": constants.UPDATE_SIMPLE_RULE_ARGUMENTS,
                        "request_body": {
                            "ruleName": ruleConfigurationKey,
                            "ruleTreeId": gLCodingRuleTreeId,
                            "ruleValidationType": "GLCoding",
                            "scac": props.scac,
                            "shipmentAccountType": props.accountType,
                            ruleArgumentsMap
                        }
                    }
                    
                   /**
                    * Concurrency Control:
                    * UPDATE_SIMPLE_RULE_ARGUMENTS api in TIPSCCS  has concurrency control to process only one request at a time.
                    * Making below calls to UPDATE_SIMPLE_RULE_ARGUMENTS sequential - one at a time.
                    */
                    await placePostRequestAsync(glData, () => {
                        setSecondaryMessages((lastVal) => {
                            return { ...lastVal, [ruleConfigurationKey]: { type: "success", content: `${ruleConfigurationKey} successfully updated!` } }
                        })
                    },
                        () => {
                            setSecondaryMessages((lastVal) => {
                                return { ...lastVal, [ruleConfigurationKey]: { type: "error", content: `${ruleConfigurationKey} update failed!` } }
                            })
                        });
                }
            }
        }
        const data = {
            "operation": constants.SAVE_ACCOUNT_CONFIGURATION,
            "request_body": {
                "scac": props.scac,
                "shipmentAccountType": props.accountType,
                "accountNumbers": accountNumbersList,
                "businessType": businessType.value,
                "contractType": contractType.value,
                "countryCode": countryCode.value,
                "chargeGroupConfigs": chargeGroupConfigs,
                "invoiceMatchPropertyConfigs": [],
                "inputVersionMap": Object.fromEntries(inputVersionMap),
                "isEnabled": enableAccountNumber
            }
        }
        const onSuccess = async (res: AxiosResponse) => {
            setSecondaryMessages({});
            setShowSaveConfirmModal(false);
            try {
                // The save call already succeeded by the time we're here; res.data is only used to
                // surface an optional warningMessage. Parse defensively and fall back to {} on failure,
                // so a malformed JSON string is NOT mistaken for a save failure (which would skip GL
                // coding + table refresh and wrongly tell the user the whole save failed).
                let responseData: any = {};
                try {
                    responseData = typeof res.data === "string" ? JSON.parse(res.data) : (res.data ?? {});
                } catch (parseErr) {
                    responseData = {};
                }
                try {
                    await saveGLDetails();
                } catch (glError) {
                    setDefaultValues();
                    setModalSeverity("warning");
                    setErrorModalMessage("Account configuration saved successfully, but GL coding update failed. Please update GL coding separately.");
                    props.getAccountConfiguration();
                    return;
                }
                setDefaultValues();
                props.getAccountConfiguration();
                if (responseData?.warningMessage) {
                    setModalSeverity("warning");
                    setErrorModalMessage(responseData.warningMessage);
                } else {
                    setMessage({
                        type: "success",
                        content: `Account(s) ${accountNumbersList.join(", ")} saved successfully to ${props.accountType}.`,
                        dismissible: true,
                        onDismiss: () => setMessage(DEFAULT_EMPTY_FLASH_MESSAGE)
                    });
                }
            } catch (e) {
                setModalSeverity("error");
                setErrorModalMessage(constants.BACKEND_EXCEPTION_TEXT);
            } finally {
                setIsSaveLoading(false);
            }
        }
        const onError = (err: AxiosError) => {
            setSecondaryMessages({});
            setShowSaveConfirmModal(false);
            setIsSaveLoading(false);
            const errorMsg = err.response?.data?.errorMessage || constants.BACKEND_EXCEPTION_TEXT;
            if (err.response?.status === 400) {
                setModalSeverity("error");
                setErrorModalMessage(errorMsg);
            } else {
                setMessage(getErrorFlashMessageProps(err));
            }
        }
        placePostRequest(data, onSuccess, onError);
    }

    function disableSearchAccountNumber() {
        setSearchAccountNumber(false);
    }

    const handelGlCodeSelection = (rule: string, option?: string) => {
        setGlSelectedData((lastValue) => {
            return { ...lastValue, [rule]: { value: option } };
        })
    } 

    return(
        <SpaceBetween direction="vertical" size="l">
            <Container header={
                <Header variant="h2">
                    Account Configuration
                </Header>
            }>
                <div>
                    {
                        props.tableLoading ?
                            <StatusIndicator type="loading">{constants.LOADING}</StatusIndicator>
                            :
                            <div>
                                {
                                    hideAccountTable ?
                                        <SpaceBetween size={"l"}>
                                            <MatrixFlashMessage content={[message]} />
                                            <MatrixFlashMessage content={Object.values(secondaryMessages)} />
                                            <Form
                                                actions={
                                                    <SpaceBetween direction="horizontal" size="xs">
                                                        <Button variant="link" onClick={() => {setHideAccountTable(false); setDefaultValues()}}>{constants.CANCEL}</Button>
                                                        <Button variant="primary" loading={isSaveLoading} onClick={validateBeforeSave}>{constants.SAVE}</Button>
                                                        <Modal
                                                            visible={showSaveConfirmModal}
                                                            onDismiss={() => setShowSaveConfirmModal(false)}
                                                            header={"Please confirm before proceeding."}
                                                            footer={
                                                                <Box float="right">
                                                                    <SpaceBetween direction="horizontal" size="xs">
                                                                        <Button variant="link" onClick={() => setShowSaveConfirmModal(false)}>{constants.CANCEL}</Button>
                                                                        <Button variant="primary" loading={isValidating || isSaveLoading} onClick={validateAndSave}>{constants.CONFIRM}</Button>
                                                                    </SpaceBetween>
                                                                </Box>
                                                            }
                                                        >
                                                            <SpaceBetween size="m">
                                                                <Alert type={"warning"} header={"Known issues / limitations"}>
                                                                    {constants.DEFAULT_ALERT_MESSAGE}
                                                                </Alert>
                                                                <Box variant="span">
                                                                    <Box variant="span" fontWeight="bold">{constants.CONFIRM}</Box>
                                                                    {" when clicked will "}
                                                                    <Box variant="span" fontWeight="bold">{"update"}</Box>
                                                                    {" values in the backend. Please click "}
                                                                    <Box variant="span" fontWeight="bold">{constants.CONFIRM}</Box>
                                                                    {" with caution."}
                                                                </Box>
                                                            </SpaceBetween>
                                                        </Modal>
                                                    </SpaceBetween>
                                                }
                                                header={
                                                    <Header
                                                        variant="h3"
                                                    >
                                                        {constants.ADD_ACCOUNTS}
                                                    </Header>
                                                }
                                            >
                                                <SpaceBetween direction="vertical" size="l">
                                                    <FormField
                                                        constraintText="Separate multiple Account numbers with comma"
                                                        errorText={accountNumbersError}
                                                        label={constants.ACCOUNT_NUMBERS}>
                                                        <Input value={accountNumbers} onBlur={() => setAccountNumbers(accountNumbers.trim())} onChange={({ detail }) => {
                                                            setAccountNumbers(detail.value);
                                                            if (detail.value.length === 0) {
                                                                setAccountNumbersError("At least one Account Number is Required");
                                                            } else {
                                                                setAccountNumbersError(constants.EMPTY_STRING);
                                                            }
                                                        }}/>
                                                    </FormField>
                                                    <div>
                                                        {constants.ACCOUNT_TYPE} : <strong> {props.accountType} </strong>
                                                    </div>

                                                    <SpaceBetween direction="vertical" size="l">
                                                        <FormField
                                                            errorText={businessTypeError}
                                                            label={constants.BUSINESS_TYPE}>
                                                            <Select
                                                                selectedOption={businessType}
                                                                onChange={({ detail }) => {
                                                                    setBusinessType(detail.selectedOption);
                                                                    setBusinessTypeError(constants.EMPTY_STRING);
                                                                }}
                                                                options={
                                                                    props.businessTypes.sort().map(option => {
                                                                        return {
                                                                            value: option
                                                                        }
                                                                    })
                                                                }
                                                                expandToViewport={true}
                                                                filteringType="auto"
                                                                selectedAriaLabel="Selected"
                                                            />
                                                        </FormField>
                                                        <FormField
                                                            errorText={contractTypeError}
                                                            label={constants.CONTRACT_TYPE}>
                                                            <Select
                                                                selectedOption={contractType}
                                                                onChange={({ detail }) => {
                                                                    setContractType(detail.selectedOption);
                                                                    setContractTypeError(constants.EMPTY_STRING);
                                                                }}
                                                                options={
                                                                    props.contractTypes.sort().map(option => {
                                                                        return {
                                                                            value: option
                                                                        }
                                                                    })
                                                                }
                                                                expandToViewport={true}
                                                                filteringType="auto"
                                                                selectedAriaLabel="Selected"
                                                            />
                                                        </FormField>
                                                        <FormField
                                                            errorText={countryCodeError}
                                                            label={constants.COUNTRY_CODE}>
                                                            <Select
                                                                selectedOption={countryCode}
                                                                onChange={({ detail }) => {
                                                                    setCountryCode(detail.selectedOption);
                                                                    setCountryCodeError(constants.EMPTY_STRING);
                                                                }}
                                                                options={
                                                                    props.countryCodes.sort().map(option => {
                                                                        return {
                                                                            value: option
                                                                        }
                                                                    })
                                                                }
                                                                expandToViewport={true}
                                                                filteringType="auto"
                                                                selectedAriaLabel="Selected"
                                                            />
                                                        </FormField>
                                                        <FormField>
                                                            <Toggle
                                                                onChange={({ detail }) => setEnableAccountNumber(detail.checked)}
                                                                checked={enableAccountNumber}>
                                                                {enableAccountNumber ?
                                                                    constants.ENABLE_ACCOUNT_MESSAGE : constants.DISABLE_ACCOUNT_MESSAGE}
                                                            </Toggle>
                                                        </FormField>
                                                        {!!glCodeDropdownData?.length &&
                                                            <SpaceBetween size={"l"}>
                                                                <hr />
                                                                <div>
                                                                    {constants.GL_SEGMENT_HEADING}
                                                                </div>
                                                                {glCodeDropdownData.map((ruleData) =>
                                                                    <FormField
                                                                        key={ruleData.rule}
                                                                        errorText={glSelectionError[ruleData.rule]}
                                                                        label={ruleData.rule}>
                                                                        <Select
                                                                            selectedOption={glSelectedData[ruleData.rule]}
                                                                            onChange={({ detail }) => {
                                                                                handelGlCodeSelection(ruleData.rule, detail.selectedOption.value);
                                                                                setGlSelectionErrorWrapper(ruleData.rule)
                                                                            }}
                                                                            options={
                                                                                ruleData.values?.sort().map((option: string) => {
                                                                                    return {
                                                                                        value: option
                                                                                    }
                                                                                })
                                                                            }
                                                                            expandToViewport={true}
                                                                            filteringType="auto"
                                                                            selectedAriaLabel="Selected"
                                                                        />
                                                                    </FormField>
                                                                )}
                                                            </SpaceBetween>
                                                        }                                                     
                                                    </SpaceBetween>
                                                    <ChargeGroupConfigsAdd chargeGroupConfigs={chargeGroupConfigs} setChargeGroupConfigs={setChargeGroupConfigs}/>
                                                </SpaceBetween>
                                            </Form>
                                        </SpaceBetween>
                                        :
                                        <SpaceBetween size={"xl"} direction={"vertical"}>
                                            <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
                                                <Input
                                                    onChange={({ detail }) => setAccountNumber(detail.value)}
                                                    onBlur={() => setAccountNumber(accountNumber.trim())}
                                                    value={accountNumber}
                                                />
                                                <Button loading={isLoading} variant="normal" onClick={filterAccountNumber}>{constants.SEARCH}</Button>
                                            </Grid>
                                            <Button variant={"normal"} onClick={() => setHideAccountTable(true)}>+ {constants.ADD_ACCOUNTS}</Button>
                                            <div>
                                                <SpaceBetween size={"l"}>
                                                    <MatrixFlashMessage content={[toggleStatusMessage]} />
                                                    {
                                                        searchAccountNumber ?
                                                            <SpaceBetween size={"xl"} direction={"vertical"}>
                                                                <AccountConfigTable
                                                                    tableData={getDataForSearchedAccount()}
                                                                    scac={props.scac}
                                                                    accountType={props.accountType}
                                                                    setPopupAccountObject={setPopupAccountObject}
                                                                    setShowAccountDetails={setShowAccountDetails}
                                                                    setShowIMPSDetails={setShowIMPSDetails}
                                                                    getAccountConfiguration={props.getAccountConfiguration}
                                                                    disableSearchAccountNumber={disableSearchAccountNumber}
                                                                    setToggleStatusMessage={setToggleStatusMessage}
                                                                />
                                                                <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                                                                    <div></div>
                                                                    <Button onClick={() => disableSearchAccountNumber()}>{constants.GO_BACK}</Button>
                                                                    <div></div>
                                                                </Grid>
                                                            </SpaceBetween> :
                                                            <SpaceBetween size={"xl"} direction={"vertical"}>
                                                                <AccountConfigTable

                                                                    tableData={getTableData()}
                                                                    scac={props.scac}
                                                                    accountType={props.accountType}
                                                                    setPopupAccountObject={setPopupAccountObject}
                                                                    setShowAccountDetails={setShowAccountDetails}
                                                                    setShowIMPSDetails={setShowIMPSDetails}
                                                                    getAccountConfiguration={props.getAccountConfiguration}
                                                                    disableSearchAccountNumber={disableSearchAccountNumber}
                                                                    setToggleStatusMessage={setToggleStatusMessage}
                                                                />
                                                                <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
                                                                    <div></div>
                                                                    <Button onClick={() => props.changeToken(-1)} disabled={props.previousDisabled}>{constants.PREVIOUS}</Button>
                                                                    <Button onClick={() => props.changeToken(1)} disabled={props.nextDisabled}>{constants.NEXT}</Button>
                                                                    <div></div>
                                                                </Grid>
                                                            </SpaceBetween>
                                                    }
                                                </SpaceBetween>
                                            </div>
                                        </SpaceBetween>
                                }
                            </div>
                    }
                </div>
            </Container>
            {
                (showAccountDetails || showIMPSDetails) ?
                    <Container
                        header={<>
                            <Header variant="h1">
                                Account: <strong>{popupAccountObject.accountNumber}</strong>
                            </Header>
                            <SpaceBetween direction="horizontal" size="xxl">
                                <Badge color={popupAccountObject.status ? "green" : "red"}>
                                    {constants.STATUS} :<strong> {popupAccountObject.status ? constants.ENABLED : constants.DISABLED} </strong>
                                </Badge>
                                <Badge
                                    color="blue"> {constants.BUSINESS_TYPE} :<strong> {popupAccountObject.businessType} </strong>
                                </Badge>
                                <Badge
                                    color="blue"> {constants.CONTRACT_TYPE} :<strong> {popupAccountObject.contractType} </strong>
                                </Badge>
                                <Badge
                                    color="blue"> {constants.COUNTRY_CODE} :<strong> {popupAccountObject.countryCode} </strong>
                                </Badge>
                            </SpaceBetween>
                        </>}
                    >
                        <SpaceBetween size={"xxl"}>
                        <Container>
                            {
                                showAccountDetails ?
                                <AccountConfigPopup
                                    scac={props.scac}
                                    accountType={props.accountType}
                                    popupAccountObject={popupAccountObject}
                                    setShowAccountDetails={setShowAccountDetails}
                                />
                                : constants.EMPTY_STRING
                            }
                        </Container>
                            {
                                showIMPSDetails ?
                                <InvoiceMatchPropertyConfigPopup
                                    scac={props.scac}
                                    showIMPSDetails={showIMPSDetails}
                                    accountType={props.accountType}
                                    setPopupAccountObject = {setPopupAccountObject}
                                    popupAccountObject={popupAccountObject}
                                    setShowIMPSDetails={setShowIMPSDetails}
                                /> : constants.EMPTY_STRING
                            }
                        </SpaceBetween>
                    </Container>
                    : constants.EMPTY_STRING
            }
            <Modal
                visible={conflictingAccounts.length > 0}
                onDismiss={() => { setConflictingAccounts([]); setShowManualDisable(false); setConflictRetryMessage(constants.EMPTY_STRING); }}
                header="Account Conflict"
                size="large"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => { setConflictingAccounts([]); setShowManualDisable(false); setConflictRetryMessage(constants.EMPTY_STRING); }}>Cancel</Button>
                            {!showManualDisable && (
                                <Button onClick={handleDisableManuallyClick}>Override Manually</Button>
                            )}
                            {showManualDisable ? (
                                <Button variant="primary" loading={isDisableAllLoading} onClick={disableSelectedAndProceed}>Confirm & Proceed</Button>
                            ) : (
                                <Button variant="primary" loading={isDisableAllLoading} onClick={disableAllConflictingAndProceed}>Override All & Proceed</Button>
                            )}
                        </SpaceBetween>
                    </Box>
                }
            >
                <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                <SpaceBetween size="s">
                    {conflictRetryMessage !== constants.EMPTY_STRING && (
                        <Alert type="error">{conflictRetryMessage}</Alert>
                    )}
                    <Alert type="warning">
                        {showManualDisable
                            ? `Toggle each account to 'Override' its existing mapping and add it to '${props.accountType}', or 'Keep Existing' to leave it unchanged. Then click 'Confirm & Proceed'.`
                            : "These account(s) already exist in another account type. Review their current status below."
                        }
                    </Alert>
                    <Table
                        columnDefinitions={[
                            {
                                id: "accountNumber",
                                header: "Account Number",
                                cell: (item: {accountNumber: string, existingAccountType: string, isDisabled: boolean}) => item.accountNumber
                            },
                            {
                                id: "scac",
                                header: "SCAC",
                                cell: () => props.scac
                            },
                            {
                                id: "existingAccountType",
                                header: "Currently In",
                                cell: (item: {accountNumber: string, existingAccountType: string, isDisabled: boolean}) => item.existingAccountType
                            },
                            {
                                id: "currentStatus",
                                header: "Current Status",
                                cell: (item: {accountNumber: string, existingAccountType: string, isDisabled: boolean}) => (
                                    item.isDisabled
                                        ? <StatusIndicator type="stopped">Disabled</StatusIndicator>
                                        : <StatusIndicator type="success">Enabled</StatusIndicator>
                                )
                            },
                            {
                                id: "targetAccountType",
                                header: "Adding To",
                                cell: () => props.accountType
                            },
                            {
                                id: "action",
                                header: "Action",
                                minWidth: 170,
                                cell: (item: {accountNumber: string, existingAccountType: string, isDisabled: boolean}) => (
                                    showManualDisable ? (
                                        <Toggle
                                            checked={manualDisableSelections.get(item.accountNumber) || false}
                                            onChange={({ detail }) => {
                                                setManualDisableSelections(prev => {
                                                    const updated = new Map(prev);
                                                    updated.set(item.accountNumber, detail.checked);
                                                    return updated;
                                                });
                                            }}
                                        >
                                            <span style={{ whiteSpace: "nowrap" }}>
                                                {manualDisableSelections.get(item.accountNumber) ? "Keep Existing" : "Override"}
                                            </span>
                                        </Toggle>
                                    ) : (
                                        <StatusIndicator type="warning">Conflict</StatusIndicator>
                                    )
                                )
                            }
                        ]}
                        items={conflictingAccounts}
                        variant="embedded"
                    />
                </SpaceBetween>
                </div>
            </Modal>
            <Modal
                visible={errorModalMessage !== constants.EMPTY_STRING && conflictingAccounts.length === 0}
                onDismiss={() => setErrorModalMessage(constants.EMPTY_STRING)}
                header={modalSeverity === "warning" ? "Warning" : "Account Mapping Error"}
                footer={
                    <Box float="right">
                        <Button variant="primary" onClick={() => setErrorModalMessage(constants.EMPTY_STRING)}>OK</Button>
                    </Box>
                }
            >
                <Alert type={modalSeverity}>{errorModalMessage}</Alert>
            </Modal>
        </SpaceBetween>
    );
}

export default AccountConfiguration;
